import React from "react";
import { FileText, Video, Music, Image, Globe, File, MessageSquareText } from "lucide-react";
import type { ContentItem } from "@/types/content";

interface RoomContentMentionPickerProps {
  contents: ContentItem[];
  isOpen: boolean;
  onSelect: (item: ContentItem) => void;
  onClose: () => void;
  filter: string;
}

const getIconForType = (type: string) => {
  switch (type) {
    case "chat": return MessageSquareText;
    case "pdf":
    case "doc":
    case "text": return FileText;
    case "youtube":
    case "video":
    case "recording": return Video;
    case "audio": return Music;
    case "image": return Image;
    case "web": return Globe;
    default: return File;
  }
};

const getIconColorForType = (type: string) => {
  switch (type) {
    case "pdf":
    case "doc":
    case "text": return "hsl(var(--primary))";
    case "youtube":
    case "video":
    case "recording": return "hsl(var(--color-incorrect))";
    case "audio": return "hsl(var(--color-premium))";
    case "web": return "hsl(var(--color-info))";
    default: return "hsl(var(--muted-foreground))";
  }
};

export const RoomContentMentionPicker: React.FC<RoomContentMentionPickerProps> = ({
  contents,
  isOpen,
  onSelect,
  onClose,
  filter,
}) => {
  if (!isOpen || contents.length === 0) return null;

  const filtered = filter
    ? contents.filter((c) =>
        c.title.toLowerCase().includes(filter.toLowerCase())
      )
    : contents;

  if (filtered.length === 0) return null;

  return (
    <div className="absolute bottom-full left-0 z-50 mb-2 w-full max-w-sm rounded-xl border border-border bg-popover p-1 shadow-lg">
      <div className="max-h-48 overflow-y-auto">
        {filtered.map((item) => {
          const Icon = getIconForType(item.type);
          const iconColor = getIconColorForType(item.type);
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                onSelect(item);
                onClose();
              }}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
            >
              <Icon
                className="h-4 w-4 flex-shrink-0"
                style={{ color: iconColor }}
              />
              <span className="truncate text-foreground">{item.title}</span>
              <span className="ml-auto text-xs text-muted-foreground capitalize">
                {item.type}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
